import { createEmptyRulebookDefinition, uniqueRulebookDraftId } from "@next-platform/client-common-core/rulebook/editor/rulebookDraftFactory";
import useRulebookDrafts from "@next-platform/client-common-core/rulebook/editor/useRulebookDrafts";
import { Button, ButtonVariant, Spinner, SpinnerSize, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import type { RulebookSummary } from "@next-platform/packages-ts-types";
import React, { memo, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import Trans from "@/components/Trans/Trans";
import { SortOptions } from "@/components/type";
import { ROUTES } from "@/constants";

import useMyRulebookEntries from "./hooks/useMyRulebookEntries";
import MyRulebookCard from "./MyRulebookCard";
import RulebookImportModal from "./RulebookImportModal";

type RulebookDraftsListProps = {
  search?: string;
  sort?: SortOptions;
  /** Rulebooks saved on the server; those a local draft publishes to are folded into that draft's card. */
  savedRulebooks?: RulebookSummary[] | null;
  isSavedLoading?: boolean;
  hasSavedError?: boolean;
  onReloadSaved?: () => void;
};

const RulebookDraftsList = ({
  search = "",
  sort = SortOptions.LATEST,
  savedRulebooks = null,
  isSavedLoading = false,
  hasSavedError = false,
  onReloadSaved,
}: RulebookDraftsListProps) => {
  const navigate = useNavigate();
  const { drafts, saveDraft, isCorrupt } = useRulebookDrafts();
  const { entries, total } = useMyRulebookEntries({ drafts, savedRulebooks, search, sort });
  const [stateIsImportOpen, setStateIsImportOpen] = useState(false);

  // While the saved side is still pending an empty grid says nothing rather than "no Rulebooks yet".
  const isListEmpty = entries.length === 0 && !isSavedLoading;
  const openImportModal = useCallback(() => setStateIsImportOpen(true), []);
  const closeImportModal = useCallback(() => setStateIsImportOpen(false), []);
  const openDraft = useCallback((id: string) => navigate(`${ROUTES.rulebookEditor}/${id}`), [navigate]);
  const createDraft = useCallback(() => {
    // Fresh drafts open straight on the Properties tab, so the name is filled in there.
    const definition = createEmptyRulebookDefinition({ id: uniqueRulebookDraftId(drafts.map((draft) => draft.definition.id)), name: "" });
    if (saveDraft(definition)) {
      navigate(`${ROUTES.rulebookEditor}/${definition.id}`);
    }
  }, [drafts, saveDraft, navigate]);

  return (
    <div data-testid="RulebookDraftsList" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Text variant={TextVariants.HEADING_SMALL_REGULAR}>
            <Trans i18nKey="rulebookEditor:list.title" />
          </Text>
          {isSavedLoading ? (
            <span data-testid="RulebookDraftsList-SavedLoading">
              <Spinner size={SpinnerSize.SMALL} />
            </span>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Button variant={ButtonVariant.CONTAINED} onClick={createDraft} data-testid="RulebookDraftsList-New">
            <Trans i18nKey="rulebookEditor:list.new" />
          </Button>
          <Button variant={ButtonVariant.OUTLINED} onClick={openImportModal} data-testid="RulebookDraftsList-Import">
            <Trans i18nKey="rulebookEditor:list.import" />
          </Button>
        </div>
      </div>
      {isCorrupt ? (
        <div data-testid="RulebookDraftsList-CorruptWarning" className="rounded-xl bg-yellow-3 p-3 text-yellow-11">
          <Trans i18nKey="rulebookEditor:list.corruptWarning" />
        </div>
      ) : null}
      {hasSavedError ? (
        <div
          data-testid="RulebookDraftsList-SavedError"
          className="flex items-center justify-between gap-3 rounded-xl bg-yellow-3 p-3 text-yellow-11"
        >
          <Trans i18nKey="rulebookEditor:list.savedError" />
          {onReloadSaved ? (
            <Button variant={ButtonVariant.OUTLINED} onClick={onReloadSaved} data-testid="RulebookDraftsList-SavedRetry">
              <Trans i18nKey="rulebookEditor:list.savedRetry" />
            </Button>
          ) : null}
        </div>
      ) : null}
      {isListEmpty ? (
        <p data-testid="RulebookDraftsList-Empty" className="py-8 text-center text-gray-11">
          {total === 0 ? <Trans i18nKey="rulebookEditor:list.empty" /> : <Trans i18nKey="rulebookEditor:list.noMatch" />}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {entries.map((entry) =>
            entry.kind === "draft" ? (
              <MyRulebookCard
                key={entry.key}
                draft={entry.draft}
                onOpen={openDraft}
                onReloadSaved={onReloadSaved}
                data-testid={`MyRulebookCard-${entry.draft.definition.id}`}
              />
            ) : (
              <MyRulebookCard
                key={entry.key}
                summary={entry.summary}
                draft={entry.draft}
                onOpen={openDraft}
                onReloadSaved={onReloadSaved}
                data-testid={`MyRulebookCard-${entry.summary.id}`}
              />
            ),
          )}
        </div>
      )}
      <RulebookImportModal isOpen={stateIsImportOpen} onClose={closeImportModal} />
    </div>
  );
};

export default memo(RulebookDraftsList);
